import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Card, CardHeader, CardTitle, CardContent, CardDescription } from '@core-ui/Components/ui/card';
import { Button } from '@core-ui/Components/ui/button';
import { IconPlus, IconPhonePlus } from '@tabler/icons-react';
import { logError } from '@lib/logger/logError';
import { logSuccess } from '@lib/logger/logSuccess';
import { logWarning } from '@lib/logger/logWarning';

import useNumbersEdgeApi from '@features/AdminConsole/hooks/useNumbersApi.ts';
import useOrganizationsEdgeApi from '@features/AdminConsole/hooks/userOrganizationsEdgeApi';
import { PaginationControls } from '@ui/shared/paginationControls';
import { usePaginationData } from '@hooks/usePaginationData';
import { SearchInput } from '@ui/shared/searchInput';

import { useBreadcrumbStore } from '@ui/Layout/store/useBreadcrumbStore';
import {
  type CreateNumberDto,
  type NumberPool,
  type NumbersForm, type SelectOption, type UpdateNumberV3Payload,
} from '@types';

import { z } from 'zod';
import type { TFunction } from 'i18next';
import { useTranslation } from 'react-i18next';
import NumbersTable from '@features/numbers/components/Numbers/NumbersTable.tsx';
import NumberDialog from '@features/numbers/components/Numbers/NumberDialog.tsx';
import ConfirmDeleteNumberDialog from '@features/numbers/components/Numbers/ConfirmDeleteDialog.tsx';
import useToastMessage from '@lib/logger/toast.ts';

interface NumberSchemas {
  createSchema: z.ZodTypeAny;
  editSchema: z.ZodTypeAny;
}

function buildNumberSchemas (t: TFunction): NumberSchemas {
  const msg = {
    numberRequired: t('adminConsole.validation.numberRequired', 'Number is required.'),
    locationRequired: t('adminConsole.validation.locationRequired', 'Location is required.'),
    numberInvalid: t('adminConsole.validation.numberInvalid', 'The number is invalid.'),
    typeRequired: t('adminConsole.validation.typeRequired', 'Number type is required.'),
    orgRequired: t('adminConsole.validation.organizationRequired', 'Organization is required')
  };

  const baseSchema = z.object({
    number:   z.string().trim().min(11, msg.numberRequired).regex(/^\+[\d\s]+$/, t('adminConsole.validation.invalidNumber')).max(20, t('adminConsole.validation.maxNumber')),
    location: z.string().trim().min(1, msg.locationRequired),
    type:     z.string().trim().min(1, msg.typeRequired ),
    active:   z.boolean(),
    ownerId:  z.string().nullable().optional(),
    ownerName:z.string().nullable().optional(),
  });

  const createSchema = baseSchema.superRefine((val, ctx) => {
    if (val.type === 'ORGANIZATION' && (val.ownerId == null)) {
      ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['ownerName'], message: t('adminConsole.validation.organizationRequired', 'Organization is required.') });
    }
    if (val.type === 'GLOBAL' && (val.ownerId != null)) {
      ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['ownerName'], message: t('adminConsole.validation.ownerMustBeEmpty', 'Organization must be empty for GLOBAL.') });
    }
  });

  const editSchema = baseSchema.superRefine((val, ctx) => {
    if (val.type === 'ORGANIZATION' && (val.ownerId == null)) {
      ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['ownerName'], message: t('adminConsole.validation.ownerRequired', 'Organization is required.') });
    }
    if (val.type === 'GLOBAL' && (val.ownerId != null)) {
      ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['ownerName'], message: t('adminConsole.validation.ownerMustBeEmpty', 'Organization must be empty for GLOBAL.') });
    }
  });

  return { createSchema, editSchema };
}

type FormErrors = Partial<Record<
  'number' | 'location' | 'ownerName' | 'type' | 'active',
  string
>>;

interface Props {
  isSuperAdmin: boolean;
}

export default function NumbersSection ( { isSuperAdmin }: Props): React.JSX.Element {
  const { t } = useTranslation();
  const  { showToast } = useToastMessage();
  const { getNumbersAsync, deleteNumberAsync, updateNumberV3Async, createNumberAsync } = useNumbersEdgeApi();
  const { getActiveOrganizationsAsync } = useOrganizationsEdgeApi();

  const [serverNumbers, setServerNumbers] = useState<NumberPool[]>([]);
  const [totalCount, setTotalCount] = useState<number>(0);
  const [loading, setLoading] = useState<boolean>(true);

  const [search, setSearch] = useState<string>('');
  const [inputValue, setInputValue] = useState<string>('');

  const [openDialog, setOpenDialog] = useState(false);
  const [editId, setEditId] = useState<string | null>(null);
  const [confirmDeleteId, setConfirmDeleteId] = useState<string | null>(null);
  const [isEdit, setIsEdit] = useState(false);

  const [form, setForm] = useState<NumbersForm>({
    number: '',
    location: '',
    type: '',
    active: false,
    ownerId: null,
    ownerName: '',
  });

  const [numTypeOptions, setNumTypeOptions] = useState<SelectOption[]>([]);
  const [orgOptions, setOrgOptions] = useState<SelectOption[]>([]);
  const [formErrors, setFormErrors] = useState<FormErrors>({});

  const clearFieldError = (field: keyof FormErrors):void => {
    setFormErrors((prev) => ({ ...prev, [field]: undefined }));
  };

  const setItems = useBreadcrumbStore((s) => s.setItems);
  useEffect(() => {
    setItems([
      { labelKey: 'breadcrumb.home', href: '/workspace/sessions/new' },
      { labelKey: 'breadcrumb.adminConsole', href: '/workspace/adminconsole' },
      { labelKey: 'breadcrumb.numbers' },
    ]);
  }, [setItems]);

  useEffect(() => {
    const typeOptions = [
      { label: t('adminConsole.numberDialog.typeOrganization', { defaultValue: 'ORGANIZATION' }), value: 'ORGANIZATION' },
      { label: 'GLOBAL', value: 'GLOBAL' }
    ];

    setNumTypeOptions(typeOptions);
  }, [t]);


  const latestSearchRef = useRef<string>('');
  useEffect(() => {
    latestSearchRef.current = search;
  }, [search]);

  const { currentPage, setCurrentPage, rowsPerPage, setRowsPerPage } = usePaginationData<NumberPool>({
    mode: 'server',
    totalItems: totalCount,
    onServerRequest: useCallback((page: number, limit: number) => {
      void fetchNumbers(page, limit, latestSearchRef.current);
    }, []),
    initialRowsPerPage: 10,
    initialPage: 1,
  });

  useEffect(() => {
    void fetchNumbers(1, rowsPerPage, search);
    setCurrentPage(1);
  }, [search, rowsPerPage]);

  const didFetchOrgs = useRef(false);
  useEffect(() => {
    if (didFetchOrgs.current) {
      return;
    }
    didFetchOrgs.current = true;
    void (async () => {
      try {
        const res = await getActiveOrganizationsAsync();
        if (res.success && Array.isArray(res.data)) {
          setOrgOptions(res.data.map((o: { id: string; name: string }) => ({
            value: String(o.id ?? ''),
            label: String(o.name ?? '—'),
          })));
        }
      } catch {}
    })();
  }, [getActiveOrganizationsAsync]);

  async function fetchNumbers (page: number, limit: number, query: string): Promise<void> {
    if (query.includes('+')) {
      query = query.replaceAll('+', '');
    }

    setLoading(true);
    try {
      const res = await getNumbersAsync(page, limit, query, []);
      if (res.data == null) {
        throw new Error(res.error?.message ?? 'Error fetching numbers');
      }

      const normalized: NumberPool[] = res.data.map((number: NumberPool) => {
        const id  = String(number.id).trim();

        return {
          id,
          number: number.number,
          location: number.location,
          type: number.type,
          ownerName: number.ownerName ?? '',
          active: number.active,
          ownerId: number.ownerId ?? null,
          timesUsed: number.timesUsed,
          deletedAt: number.deletedAt ?? null,
        } satisfies NumberPool;
      });

      setServerNumbers(normalized);
      setTotalCount(res.totalCount ?? 0);
    } catch (err) {
      console.error(err);
      setServerNumbers([]);
      setTotalCount(0);
      await logError(
        'Error fetching numbers',
        'NumbersTab.fetchNumbers',
        { page, limit, query, error: String((err as Error)?.message ?? err) },
        true
      );
    } finally {
      setLoading(false);
    }
  }

  function openCreate (): void {
    setForm({
      number: '',
      location: '',
      type: '',
      ownerName: null,
      ownerId: null,
      active: false,
    });
    setFormErrors({});
    setEditId(null);
    setOpenDialog(true);
    setIsEdit(false);
  }

  function openEdit (number: NumberPool): void {
    setForm({
      id: number.id,
      number: number.number ?? '',
      location: number.location ?? '',
      type: number.type,
      ownerName: number.ownerName ?? null,
      ownerId: number.ownerId ?? null,
      active: number.active ?? false,
    });
    setFormErrors({});
    setIsEdit(true);
    setEditId(number.id);
    setOpenDialog(true);
  }

  function mapZodErrors (issues: z.ZodIssue[]): FormErrors {
    const out: FormErrors = {};
    for (const issue of issues) {
      const field = issue.path[0] as keyof FormErrors | undefined;

      if (field !== undefined && out[field] === undefined) {
        out[field] = issue.message;
      }
    }
    return out;
  }

  async function handleSave (): Promise<void> {
    try {
      const { createSchema, editSchema } = buildNumberSchemas(t);
      const schema = isEdit ? editSchema : createSchema;

      const parsed = schema.safeParse(form);
      if (!parsed.success) {
        const fieldErrors = mapZodErrors(parsed.error.issues);
        setFormErrors(fieldErrors);
        const first = parsed.error.issues[0]?.path?.[0] as keyof FormErrors | undefined;
        if (first !== undefined) {
          await logWarning(
            fieldErrors[first] ?? t('adminConsole.validation.generic', 'Please fix the errors.'),
            'NumbersTab.handleSave.validation',
            { field: first, issues: parsed.error.issues },
            true
          );
        }
        return;
      }

      setLoading(true);

      if (editId !== null) {
        const payload: UpdateNumberV3Payload = {
          id: editId,
          number: (form.number ?? '').trim(),
          location: (form.location ?? '').trim(),
          type: (form.type ?? ''),
          active: (form.active ?? null),
          ownerId: form.ownerId ?? null,
        };

        const res = await updateNumberV3Async(payload);
        if (!res.success) {
          throw new Error(res.error?.message ?? 'Update failed');
        }
        await logSuccess(
          t('adminConsole.numberDialog.updated', { lng: 'en' }),
          'NumbersTab.handleSave.update',
          { idNumberEdited: editId, numberEdited: form.number, locationEdited: form.location, typeEdited: form.type, activeEdited: form.active },
          false
        );
        showToast('success', t('adminConsole.numberDialog.updated', 'Number updated successfully'));
      } else {
        const createPayload: CreateNumberDto = {
          number: (form.number ?? '').trim(),
          location: (form.location ?? '').trim(),
          type: (form.type ?? ''),
          active: (form.active ?? null),
          ownerId: form.ownerId ?? null,
          deletedAt: null
        };

        const res = await createNumberAsync(createPayload);
        if (!res.success) {
          throw new Error(res.error?.message ?? 'Create failed');
        }
        await logSuccess(
          t('adminConsole.numberDialog.created', { lng: 'en' }),
          'NumbersTab.handleSave.create',
          { idNumberCreated: form.id, numberCreated: form.number, typeNumberCreated: form.type, activeNumberCreated: form.active, locationNumberCreated: form.location },
          false
        );
        showToast('success', t('adminConsole.numberDialog.created', 'Number created successfully'));
      }

      setForm((prev) => ({ ...prev }));
      setFormErrors({});
      setOpenDialog(false);
      await fetchNumbers(currentPage, rowsPerPage, latestSearchRef.current);
    } catch (e) {
      console.error(e);
      await logError(
        t('adminConsole.numberDialog.saveError', 'There was a problem saving number.'),
        'NumbersTab.handleSave',
        { editId },
        true
      );
    } finally {
      setLoading(false);
    }
  }

  async function handleDelete (id: string | null): Promise<void> {
    if (id == null) {
      return;
    }
    try {
      setLoading(true);
      const res = await deleteNumberAsync(id);
      if (!res.success) {
        throw new Error(res.error?.message);
      }
      await logSuccess(
        t('adminConsole.numberDeleteDialog.success', { lng: 'en' }),
        'NumbersTab.handleDelete',
        { idUserDeleted: id },
        false
      );
      showToast('success', t('adminConsole.numberDeleteDialog.success', 'Number deleted successfully'));
      await fetchNumbers(currentPage, rowsPerPage, latestSearchRef.current);
    } catch (err) {
      await logError(
        t('adminConsole.numberDeleteDialog.error', 'There was a problem deleting number'),
        'NumbersTab.handleDelete',
        { idUserDeleted: id, error: String((err as Error)?.message ?? err) },
        true
      );
    } finally {
      setLoading(false);
    }
  }

  return (
    <Card className="p-6 rounded-2xl shadow-lg border border-border bg-card text-foreground">
      <CardHeader className="flex flex-col px-0 pt-0 pb-0">
        <div className="flex flex-row items-center">
          <IconPhonePlus className="font-bold h-5 w-5" />
          <CardTitle data-qa-id="number-title" className="text-2xl font-bold text-foreground ml-1">{t('adminConsole.tabs.numbers', 'Numbers')}</CardTitle>
        </div>
        <CardDescription className="px-0 m-0">
          {t('adminConsole.numbersTable.numDescription')}
        </CardDescription>
      </CardHeader>

      <CardContent className="px-0 pt-0">
        <div className="flex flex-col md:flex-row justify-between items-center mb-4 gap-2">
          <SearchInput
            value={inputValue}
            onChange={(e) => {
              setInputValue(e.target.value);
            }}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                setSearch(e.currentTarget.value);
              }
            }}
            onClick={() => {
              setSearch(inputValue);
            }}
            onClear={() => {
              setInputValue('');
              setSearch('');
            }}
            placeholder={t('adminConsole.numbersTable.searchNumber')}
            section="numbers"
          />

          {isSuperAdmin &&
            <Button
              data-qa-id="create-number-button"
              className="bg-primary hover:bg-primary/90 text-primary-foreground cursor-pointer"
              onClick={openCreate}
            >
              <IconPlus className="h-5 w-5 " />
              {t('adminConsole.numbersTable.createNumber')}
            </Button>
          }
        </div>

        <NumbersTable
          loading={loading}
          numbers={serverNumbers}
          onEdit={openEdit}
          onAskDelete={(id) => {
            setConfirmDeleteId(id ?? null);
          }}
          skeletonRows={rowsPerPage}
          isSuperAdmin={isSuperAdmin}
          isCallerId={false}
        />

        <PaginationControls
          mode="server"
          totalItems={totalCount}
          currentPage={currentPage}
          onPageChange={(p) => {
            setCurrentPage(p);
            void fetchNumbers(p, rowsPerPage, latestSearchRef.current);
          }}
          rowsPerPage={rowsPerPage}
          setRowsPerPage={(n) => {
            setRowsPerPage(n);
            setCurrentPage(1);
            void fetchNumbers(1, n, latestSearchRef.current);
          }}
          isLoading={loading}
          className="mt-4"
        />
      </CardContent>

      <NumberDialog
        open={openDialog}
        setOpen={(v) => {
          setOpenDialog(v);
          if (!v) {
            setFormErrors({});
          }
        }}
        form={form}
        setForm={(f) => {
          setForm(f);
        }}
        onSave={() => {
          void handleSave();
        }}
        orgOptions={orgOptions}
        typeOptions={numTypeOptions}
        isEdit={isEdit}
        errors={formErrors}
        clearError={clearFieldError}
        isSuperAdmin={isSuperAdmin}
      />

      <ConfirmDeleteNumberDialog
        open={confirmDeleteId !== null}
        number={serverNumbers.find(u => u.id === confirmDeleteId)?.number ?? undefined}
        onCancel={() => {
          setConfirmDeleteId(null);
        }}
        onConfirm={() => {
          if ((confirmDeleteId?.trim()) != null) {
            void handleDelete(confirmDeleteId);
          }
          setConfirmDeleteId(null);
        }}
      />
    </Card>
  );
}